// rounds/configuration — publish readiness (`GET /intake-rounds/{id}/readiness/`)
import { z } from "zod";

/**
 * The sections `readiness_items()` reports, from `modules/acquire/services.py`.
 *
 * ⚠ WAS FIVE, IS EIGHT. `communications`, `milestones` and `competencies` were always returned by
 * the backend and simply not recognised here, so every gap in them fell through to the unlabelled
 * fallback row — including `competency_coverage`, the check most likely to actually block a round.
 * There are NINE checks across these eight sections: `general` carries two (`general_configured`
 * and `applicant_configuration_ready`).
 *
 * Kept as a plain list of constants rather than an enum on the wire schema: a section we do not
 * recognise must render as an unlinked row, never fail the parse and blank the whole checklist.
 */
export const READINESS_SECTIONS = [
  "general",
  "eligibility",
  "documents",
  "questions",
  "competencies",
  "templates",
  "communications",
  "milestones",
] as const;

export type ReadinessSection = (typeof READINESS_SECTIONS)[number];

/**
 * Extra context on an unmet item, and the reason this screen can name individual gaps rather than
 * just failing sections.
 *
 * Four checks populate it:
 *   - `templates_assigned` → `missing: ["<slot>:<locale>"]`, so three entries per unassigned slot
 *   - `communications_configured` → `missing: ["reply_to_email" | "recipient_email"]`
 *   - `milestones_configured` → `invalid: ["<kind>"]`
 *   - `competency_coverage` → `uncovered: [{ code, side, name_fr, name_ar, name_en }]`
 *
 * Every field is optional and every unknown key is ignored: `error_response()` spreads details into
 * the response body, so this same shape has to survive arriving on a `409 round_not_ready` too.
 */
const UncoveredCompetencySchema = z.object({
  code: z.string(),
  side: z.string(),
  name_fr: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
});

const ReadinessDetailsSchema = z.object({
  missing: z.array(z.string()).optional(),
  invalid: z.array(z.string()).optional(),
  uncovered: z.array(UncoveredCompetencySchema).optional(),
});

export type UncoveredCompetency = z.infer<typeof UncoveredCompetencySchema>;

/**
 * One readiness check.
 *
 * `section` is `z.string()`, not an enum, on purpose — see {@link READINESS_SECTIONS}. `details`
 * defaults to `{}` because the backend omits nothing but sends an empty object for met items.
 */
export const ReadinessItemSchema = z.object({
  key: z.string(),
  met: z.boolean(),
  section: z.string(),
  resolution_code: z.string(),
  details: ReadinessDetailsSchema.default({}),
});

export type ReadinessItem = z.infer<typeof ReadinessItemSchema>;

export const ReadinessSchema = z.object({
  ready: z.boolean(),
  items: z.array(ReadinessItemSchema),
});

export type Readiness = z.infer<typeof ReadinessSchema>;

/** Narrow an unknown section string to one we have copy and a destination for. */
export function toReadinessSection(value: string): ReadinessSection | null {
  return READINESS_SECTIONS.find((section) => section === value) ?? null;
}

/**
 * The `409 round_not_ready` body: `{"code": "round_not_ready", "items": [...]}`, because
 * `error_response()` merges an error's `details` into the top level rather than nesting it.
 *
 * Parsed leniently — a publish that failed must always produce a message, so an unreadable body
 * degrades to "no items" instead of throwing on top of the error it was explaining.
 */
export function unmetItemsFrom(body: unknown): ReadinessItem[] {
  const parsed = z
    .object({ items: z.array(ReadinessItemSchema) })
    .safeParse(body);
  if (!parsed.success) return [];
  return parsed.data.items.filter((item) => !item.met);
}

// ── Gaps: one row per thing to fix, not one per failing check ────────────────────────────────

/**
 * A single actionable gap.
 *
 * ⚠ THIS IS A FANNING-OUT, and it is the whole point of the screen. A readiness ITEM is one check
 * over a whole section — "competency coverage is not satisfied" — which tells an administrator that
 * something is wrong and nothing about what. The design instead lists « Potentiel de leadership —
 * compétence obligatoire sans aucune question requise » beside its own button, once per competency.
 * That granularity is available: four of the nine checks carry `details`, so one failing item can
 * become several gaps.
 *
 * `subject` is the thing named in the row (a competency name, a template slot, a milestone kind);
 * `null` where the check has no detail and the gap is the whole section.
 */
export interface ReadinessGap {
  section: ReadinessSection | null;
  /** The backend's own check key, so an unrecognised section still identifies itself. */
  key: string;
  /** i18n key for the sentence describing the gap. */
  messageKey: ReadinessGapMessage;
  /** Interpolated into `messageKey`, and shown on the button as the destination. */
  subject: string | null;
}

/**
 * Every message a gap can carry, as literals.
 *
 * ⚠ THE UNION IS LOAD-BEARING, not decoration. `t()` type-checks against known keys and rejects a
 * plain `string`, so typing `ReadinessGap.messageKey` as `string` compiles here and fails at every
 * call site. Deriving the union from this list means a new gap message cannot drift from the type.
 */
export const READINESS_GAP_MESSAGES = [
  "round-create.readiness.gap.general",
  "round-create.readiness.gap.applicant-configuration",
  "round-create.readiness.gap.questions",
  "round-create.readiness.gap.eligibility",
  "round-create.readiness.gap.documents",
  "round-create.readiness.gap.competency",
  "round-create.readiness.gap.competency-generic",
  "round-create.readiness.gap.template",
  "round-create.readiness.gap.templates-generic",
  "round-create.readiness.gap.communication-field",
  "round-create.readiness.gap.communications-generic",
  "round-create.readiness.gap.milestone",
  "round-create.readiness.gap.milestones-generic",
  "round-create.readiness.gap.unknown",
] as const;

export type ReadinessGapMessage = (typeof READINESS_GAP_MESSAGES)[number];

/** Message key per check, used when there is no per-item detail to fan out. */
const WHOLE_SECTION_MESSAGES: Record<string, ReadinessGapMessage> = {
  general_configured: "round-create.readiness.gap.general",
  applicant_configuration_ready:
    "round-create.readiness.gap.applicant-configuration",
  questions_configured: "round-create.readiness.gap.questions",
  eligibility_saved: "round-create.readiness.gap.eligibility",
  documents_configured: "round-create.readiness.gap.documents",
  competency_coverage: "round-create.readiness.gap.competency-generic",
  templates_assigned: "round-create.readiness.gap.templates-generic",
  communications_configured:
    "round-create.readiness.gap.communications-generic",
  milestones_configured: "round-create.readiness.gap.milestones-generic",
};

/**
 * Expand the unmet checks into the gap rows the screen lists.
 *
 * Met items produce nothing — a satisfied section renders as « Complet » from the item itself, not
 * from a gap. An unmet item with usable detail fans out; an unmet item without falls back to one
 * whole-section row, and an unmet item whose key we do not know at all still produces a row, using
 * the key as its own message. A blocking check that renders as nothing would be the worst possible
 * failure on this particular screen.
 */
export function toReadinessGaps(
  items: readonly ReadinessItem[],
  locale: "fr" | "ar" | "en"
): ReadinessGap[] {
  const gaps: ReadinessGap[] = [];

  for (const item of items) {
    if (item.met) continue;
    const section = toReadinessSection(item.section);

    const push = (messageKey: ReadinessGapMessage, subject: string | null) => {
      gaps.push({ section, key: item.key, messageKey, subject });
    };

    const uncovered = item.details.uncovered ?? [];
    if (uncovered.length > 0) {
      for (const competency of uncovered) {
        const name =
          locale === "ar"
            ? competency.name_ar
            : locale === "en"
              ? competency.name_en
              : competency.name_fr;
        push("round-create.readiness.gap.competency", name || competency.code);
      }
      continue;
    }

    const invalid = item.details.invalid ?? [];
    if (invalid.length > 0) {
      for (const kind of invalid) {
        push("round-create.readiness.gap.milestone", kind);
      }
      continue;
    }

    const missing = item.details.missing ?? [];
    if (missing.length > 0) {
      // `templates_assigned` reports `"<slot>:<locale>"`, three per unassigned slot. Collapsed to
      // the slot, because the administrator picks a template once for all three locales — listing
      // the same contact point three times would treble the gap count for one decision.
      const subjects = [
        ...new Set(missing.map((entry) => entry.split(":")[0] ?? entry)),
      ];
      const messageKey =
        item.key === "templates_assigned"
          ? "round-create.readiness.gap.template"
          : "round-create.readiness.gap.communication-field";
      for (const subject of subjects) push(messageKey, subject);
      continue;
    }

    push(
      WHOLE_SECTION_MESSAGES[item.key] ?? "round-create.readiness.gap.unknown",
      null
    );
  }

  return gaps;
}
